
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { add } from 'date-fns'
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup'
import produce from 'immer';
import Select from 'react-select';
import * as Yup from 'yup';
import clsx from 'clsx';
import { toast } from 'components/Shared/Toast';
import { ListGroupItem, ListGroup, Button, CardHeader, FormGroup, FormFeedback, Input, CustomInput } from 'reactstrap';
import styled from 'styled-components'

import bootbox from 'components/Modals/Bootbox';
import UpgradePlan from 'components/Shared/UpgradePlan'
import DatePicker from 'components/Shared/DatePicker'
import { useEditorState, useEditorActions } from '../../editor-context'
import { range, zeroPad } from 'utils'
import { useProfileAbility } from 'context/profile'
import { reactSelectStyles } from 'components/Shared/ReactSelectStyles'


export function NotificationList() {
    const {t} = useTranslation()
    const profileAbility = useProfileAbility();
    const canSetNotifications = profileAbility.can('SET_NOTIFICATIONS'); // can SET_NOTIFICATIONS
    const { document: doc, mode } = useEditorState();
    const { addNotification } = useEditorActions();

    const handleAdd = () => {
      const emails = Array.from(new Set(doc.participants.map(p => p.email).filter(p => p && p.length)));
      const today = new Date();

      addNotification({
        notification: { 
          name: '', 
          recipients: emails, 
          type: 'onetime',
          frequency: 'daily',
          sendTime: '08',
          sendDate: today,
          startDate: today,
          endDate: add(today, { years: 1 }),
          sendDay: '1',
          message:  t('editor.notifications.message_placeholder'), 
          active: true 
        }
      })
    }

  
    if (!canSetNotifications) {
      return <UpgradePlan inverted />
    }

    return (
        <ListGroup className="bg-light text-dark rounded-0 p-0">
                  {
                    doc.notifications.map(notification => (
                      <NotificationItem key={notification._id} notification={notification} editorMode={mode} />
                    ))
                  }
                  {
                    !doc.notifications.length && 
                    <ListGroupItem className="bg-light p-2">
                      {t('editor.notifications.empty')}
                    </ListGroupItem>
                  }
                <div className="text-center m-2">
                    <Button color="light" onClick={handleAdd} size="sm">
                        <i className="fas fa-plus"></i> {t('editor.notifications.add')}
                    </Button>
                </div>
            </ListGroup>
    )
}

const SelectSchema = Yup.object().shape({
  label: Yup.string(),
  value: Yup.string()
});

const OneTimeNotificationSchema = Yup.object().shape({
  name: Yup.string()
  .required('Required'),
  message: Yup.string()
  .required('Required'),
  type: SelectSchema.required('Required'),
  sendDate: Yup.string()
  .required('Required'),
  sendTime: SelectSchema.required('Required'),
  recipients: Yup.array().of(Yup.string()).required('Required'),
});

const DailyNotificationSchema = Yup.object().shape({
  name: Yup.string()
  .required('Required'),
  message: Yup.string()
  .required('Required'),
  type: SelectSchema.required('Required'),
  sendTime: SelectSchema.required('Required'),
  startDate: Yup.string()
  .required('Required'),
  endDate: SelectSchema.required('Required'),
  recipients: Yup.array().of(Yup.string()).required('Required'),
});


const WeeklyNotificationSchema = Yup.object().shape({
  name: Yup.string()
  .required('Required'),
  message: Yup.string()
  .required('Required'),
  type: SelectSchema.required('Required'),
  sendTime: SelectSchema.required('Required'),
  sendDay: SelectSchema.required('Required'),
  recipients: Yup.array().of(Yup.string()).required('Required'),
});

const MonthlyNotificationSchema = Yup.object().shape({
  name: Yup.string()
  .required('Required'),
  message: Yup.string()
  .required('Required'),
  type: SelectSchema.required('Required'),
  sendTime: SelectSchema.required('Required'),
  sendDay: SelectSchema.required('Required'),
  recipients: Yup.array().of(Yup.string()).required('Required'),
});

const NotificationSchema = Yup.lazy(notification => {
  if (notification.type === 'onetime') {
    return OneTimeNotificationSchema;
  } else if (notification.frequency === 'daily') {
    return DailyNotificationSchema
  } else if (notification.frequency === 'weekly') {
    return WeeklyNotificationSchema
  }
  
  return MonthlyNotificationSchema;
})


function parseData(data) {
  return {
    ...data,
    type: data.type.value,
    sendTime: data.sendTime?.value,
    sendDay: data.sendDay?.value,
    frequency: data.frequency?.value,
  }
}

function NotificationItem({ notification, editorMode }) {
    const {t, i18n} = useTranslation()
    const [isEditable, setIsEditable] = useState(!notification.name);
    const { updateNotification, removeNotification, sendNotification } = useEditorActions();
    const { register, control, watch, handleSubmit, errors } = useForm({
      resolver: yupResolver(NotificationSchema)
    })
    const { sendTypesMap } = useSendTypesList()
    const { frequencyMap } = useFrequencyList()
    const { daysMap } = useDayList()
    const { weeksMap } = useWeekList()
    const { timesMap } = useTimeList()
  
    const auto = false;
    const watchedType = watch('type', sendTypesMap.get(notification.type));
    const type = watchedType?.value;
    const watchedFrequency = watch('frequency', frequencyMap.get(notification.frequency));
    const frequency = watchedFrequency?.value;
    
    const toggleEditable = () => setIsEditable(editable => !editable)

    const handleRemove = () => {
      removeNotification({ notificationId: notification._id })
    }

    const handleSave = async (data) => {
      const updatedNotification = parseData(data);
      const recipients = updatedNotification.recipients || [];
      
      if (!recipients.length) return toast.warning(t('editor.notifications.missing_recipients'));

      updateNotification({
        notification: {
          ...notification,
          ...updatedNotification
        }
      }, {
        onSuccess: () => setIsEditable(false)
      })
      
    }

    const handleSend = async (data) => {
      const updatedNotification = parseData(data);
      const recipients = updatedNotification.recipients || [];
      
      if (!recipients.length) return toast.warning(t('editor.notifications.missing_recipients'));
      
      const resp = await bootbox.confirm(t('editor.notifications.send_now_confirm'))
      
      if (!resp) return
      
      updateNotification({
        notification: {
          ...notification,
          ...updatedNotification
        }
      }, {
        onSuccess: () => {
          sendNotification({ notificationId: notification._id })
          setIsEditable(false)
        }
      })
    }
  

    return (
        <ListGroupItem className="bg-light p-0 ">
            <div className="notification-item-content d-flex border-bottom">
              <div className="d-flex flex-column w-100 p-2 justify-content-center">
                <span className="notification-name text-dark w-100 mb-2">
                    {notification.name || t('editor.notifications.new')}
                    <i className={`fas fa-circle ml-2 text-${notification.active ? 'success' : 'gray'}`}></i>
                </span>
                <small className="notification-value">
                    {type === 'onetime' ? 
                        t('editor.notifications.onetime') : 
                        `${t('editor.notifications.recurrent' )}/${t(frequency)}` 
                    }
                </small>
              </div>
              {!auto && 
                  <div className="text-right d-flex flex-column">
                      <Button color="light" size="sm" data-action="delete" onClick={handleRemove}>
                          <i className="fas fa-times text-danger"></i>
                      </Button>
                      <Button color="light" size="sm" data-action="edit" onClick={toggleEditable}>
                          <i className="fas fa-pen"></i>
                      </Button>
                  </div>
            }
            </div>
            {!auto && isEditable &&
                <div className="notification-item-editor bg-light-contrast p-2">
                  <form>
                      <div className="d-flex justify-content-end align-items-center">
                          <Controller
                              control={control}
                              name="active" 
                              defaultValue={notification.active}
                              render={({ onChange, onBlur, value, name }) => (
                                <CustomInput 
                                      className="mt-1 text-dark"
                                      type="switch" 
                                      onBlur={onBlur}
                                      checked={value}
                                      onChange={onChange}
                                      name="active" 
                                      label={t('editor.notifications.activate')}
                                  />
                              )}
                          />
                                    
                      </div>
                    <FormGroup>
                      <label>{t('editor.notifications.name')}:</label>
                      <Input 
                        type="text" 
                        placeholder={t('editor.notifications.name_placeholder')} 
                        name="name" 
                        innerRef={register}
                        invalid={errors.name}
                        defaultValue={notification.name}
                      />
                      <FormFeedback invalid={errors.name}>{t(`errors.${errors.name?.message}`)}</FormFeedback>
                    
                      
                    </FormGroup>
                    <div className="form-group position-relative">
                      <label>{t('editor.notifications.message')}:</label>
                      <textarea
                        className={clsx('form-control noresize')}
                        placeholder={t('editor.notifications.message_placeholder')} 
                        name="message"
                        ref={register}
                        defaultValue={notification.message}
                      />
                      <FormFeedback invalid={errors.message}>{t(`errors.${errors.message?.message}`)}</FormFeedback>
                      
                    </div>
                    <FormGroup>
                      <label>{t('editor.notifications.send_type')}:</label>
                      <Controller
                        control={control}
                        name="type" 
                        defaultValue={sendTypesMap.get(notification.type)}
                        render={({ onChange, onBlur, value}) => (
                            <SendTypesSelect
                                onChange={onChange}
                                className={clsx(errors.type && 'is-invalid')}
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        width: '100%',
                                    }),
                                    ...reactSelectStyles
                                }}
                                value={value}
                                onBlur={onBlur}
                                // isDisabled={!isEditable}
                            />
                        )}
                      />
                      <FormFeedback invalid={errors.type}>{t(`errors.${errors.type?.message}`)}</FormFeedback>
                    </FormGroup>

                    {type === 'onetime' &&
                      <div className="d-flex">
                        <FormGroup style={{position: 'relative', flex:1, marginRight: 3}}>
                          <label>{t('editor.notifications.date')}:</label>
                          <Controller
                              control={control}
                              name="sendDate" 
                              defaultValue={notification.sendDate}
                              rules={{ required: true }}
                              render={({ onChange, onBlur, value}) => (
                                  <DatePicker
                                      locale={i18n.language}
                                      selected={value ? new Date(value) : null}
                                      onChange={(date) => {
                                          onChange(date)
                                      }}
                                      onBlur={onBlur}
                                      data-action="set-link-expiration" 
                                      customInput={<input className={clsx('form-control', errors.sendDate && 'is-invalid')} type="text" style={{height: '40px'}} />}
                                      dateFormat="dd/MMM/yyyy"
                                      placeholderText={t('editor.notifications.date_placeholder')} 
                                  />
                              )}
                          />
                          <FormFeedback invalid={errors.sendDate}>{t(`errors.${errors.sendDate?.message}`)}</FormFeedback>
                          
                        </FormGroup>
                        <FormGroup style={{position: 'relative', flex:1, marginRight: 3}}>
                          <label>{t('editor.notifications.time')}:</label>
                          <Controller
                              control={control}
                              name="sendTime" 
                              defaultValue={timesMap.get(notification.sendTime)}
                              render={({ onChange, onBlur, value}) => (
                                  <TimesSelect
                                     startTime={8}
                                     endTime={20}
                                     onChange={onChange}
                                     onBlur={onBlur}
                                     value={value}
                                  />
                              )}
                          />             
                        </FormGroup>
                      </div>
                    }

                    {type === 'recurrent' &&
                    <>
                      <FormGroup>
                        <label>{t('editor.notifications.frequency')}:</label>
                        <Controller
                            control={control}
                            name="frequency" 
                            defaultValue={frequencyMap.get(notification.frequency)}
                            render={({ onChange, onBlur, value}) => (
                                <FrequencySelect
                                    onChange={onChange}
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            width: '100%',
                                        }),
                                    }}
                                    value={value}
                                    onBlur={onBlur}
                                    // isDisabled={!isEditable}
                                />
                            )}
                        />
                      </FormGroup>
                      <>
                        {frequency === 'daily' &&
                            <div className="d-flex flex-column">
                              <FormGroup style={{
                                  position: 'relative', 
                                  flex:1, 
                                  marginRight: 3, 
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                  <label>{t('editor.notifications.startDate')}:</label>
                                  <div>
                                    <Controller
                                      control={control}
                                      name="startDate" 
                                      defaultValue={notification.startDate}
                                      render={({ onChange, onBlur, value}) => (
                                          <DatePicker
                                              locale={i18n.language}
                                              selected={value ? new Date(value) : null}
                                              onChange={(date) => {
                                                  onChange(date)
                                              }}
                                              onBlur={onBlur}
                                              customInput={<input className="form-control" type="text" style={{ maxWidth: 150 }} />}
                                              dateFormat="dd/MMM/yyyy"
                                              placeholderText={t('editor.notifications.startDate')} 
                                          />
                                      )}
                                    />
                                  </div>
                              </FormGroup>
                              <FormGroup style={{
                                  position: 'relative', 
                                  flex:1, 
                                  marginRight: 3, 
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                  <label>{t('editor.notifications.endDate')}:</label>
                                  <div>
                                    <Controller
                                      control={control}
                                      name="endDate" 
                                      defaultValue={notification.endDate}
                                      render={({ onChange, onBlur, value}) => (
                                          <DatePicker
                                              locale={i18n.language}
                                              selected={value ? new Date(value) : null}
                                              onChange={(date) => {
                                                  onChange(date)
                                              }}
                                              onBlur={onBlur}
                                              customInput={<input className="form-control" type="text" style={{ maxWidth: 150 }} />}
                                              dateFormat="dd/MMM/yyyy"
                                              placeholderText={t('editor.notifications.endDate')} 
                                          />
                                      )}
                                    />
                                  </div>
                              </FormGroup>
                              <FormGroup style={{
                                  position: 'relative', 
                                  flex:1, 
                                  marginRight: 3, 
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                  <label>{t('editor.notifications.time')}:</label>
                                  <Controller
                                    control={control}
                                    name="sendTime" 
                                    defaultValue={timesMap.get(notification.sendTime)}
                                    render={({ onChange, onBlur, value}) => (
                                        <TimesSelect
                                            onChange={onChange}
                                            startTime={8}
                                            endTime={20}
                                            styles={{
                                                container: (provided) => ({
                                                    ...provided,
                                                    width: '100%',
                                                    maxWidth: 150,
                                                }),
                                                control: (provided) => ({
                                                    ...provided,
                                                    width: '100%',
                                                    maxWidth: 150,
                                                }),
                                            }}
                                            value={value}
                                            onBlur={onBlur}
                                            // isDisabled={!isEditable}
                                        />
                                    )}
                                  />
                              </FormGroup>
                            </div>
                        }
                        {frequency === 'weekly' &&
                            <div className="d-flex">
                            <FormGroup style={{position: 'relative', flex:1, marginRight: 3}}>
                                <label>{t('editor.notifications.sendDay')}:</label>
                                <Controller
                                    control={control}
                                    name="sendDay" 
                                    defaultValue={weeksMap.get(zeroPad(notification.sendDay, 2))}
                                    render={({ onChange, onBlur, value}) => (
                                        <WeekSelect
                                            onChange={onChange}
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    width: '100%',
                                                }),
                                            }}
                                            value={value}
                                            onBlur={onBlur}
                                            // isDisabled={!isEditable}
                                        />
                                    )}
                                  />
                            </FormGroup>
                            <FormGroup style={{position: 'relative', flex:1, marginRight: 3}}>
                                <label>{t('editor.notifications.time')}:</label>
                                <Controller
                                    control={control}
                                    name="sendTime" 
                                    defaultValue={timesMap.get(notification.sendTime)}
                                    render={({ onChange, onBlur, value}) => (
                                        <TimesSelect
                                            onChange={onChange}
                                            startTime={8}
                                            endTime={20}
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    width: '100%',
                                                }),
                                            }}
                                            value={value}
                                            onBlur={onBlur}
                                            // isDisabled={!isEditable}
                                        />
                                    )}
                                  />
                            </FormGroup>
                            </div>
                        }
                        {frequency === 'monthly' &&
                            <div className="d-flex">
                            <FormGroup style={{position: 'relative', flex:1, marginRight: 3}}>
                                <label>{t('editor.notifications.sendDay')}:</label>
                                <Controller
                                    control={control}
                                    name="sendDay" 
                                    defaultValue={daysMap.get(notification.sendDay)}
                                    render={({ onChange, onBlur, value}) => (
                                        <DaysSelect
                                            onChange={onChange}
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    width: '100%',
                                                }),
                                            }}
                                            value={value}
                                            onBlur={onBlur}
                                            // isDisabled={!isEditable}
                                        />
                                    )}
                                  />
                                {/* <select className="form-control input-sm" name="sendDay">
                                {{#for 1 32 1}}
                                    <option value="{{lpad this 2 '0'}}" {{#is (lpad ../sendDay 2 '0') (lpad this 2 '0')}}selected{{/is}}>{{lpad this 2 '0'}}</option>
                                {{/for}}
                                </select> */}
                            </FormGroup>
                            <FormGroup style={{position: 'relative', flex:1, marginRight: 3}}>
                                <label>{t('editor.notifications.time')}:</label>
                                <Controller
                                    control={control}
                                    name="sendTime" 
                                    defaultValue={sendTypesMap.get(notification.sendTime)}
                                    render={({ onChange, onBlur, value}) => (
                                        <TimesSelect
                                            onChange={onChange}
                                            startTime={8}
                                            endTime={20}
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    width: '100%',
                                                }),
                                            }}
                                            value={value}
                                            onBlur={onBlur}
                                            // isDisabled={!isEditable}
                                        />
                                    )}
                                  />
                            </FormGroup>
                            </div>
                        }
                      </>
                      </>
                    }


                    <FormGroup>
                      <label>{t('editor.notifications.recipients')}:</label>
                      <Controller
                        control={control}
                        name="recipients" 
                        defaultValue={notification.recipients}
                        render={({ onChange, onBlur, value}) => (
                          <InputTag 
                            tags={value}
                            className={clsx(errors.recipients && 'is-invalid')}
                            onChange={(newValue) =>  {
                              onChange(newValue)
                            }}
                            placeholder={t('editor.notifications.recipients_placeholder')} 
                          />
                        )}
                      />
                      <FormFeedback invalid={errors.recipients? 'true': 'false'}>{t(`errors.${errors.recipients?.message}`)}</FormFeedback>
                      <small className="text-danger"> {errors.recipients && t(`errors.${errors.recipients?.message}`)}</small>
                    </FormGroup>
                  </form>
                  <div className="text-right m-t">
                    {type ==='onetime' && editorMode === 'contract' &&
                      <Button className="sm" color="secondary-light" data-action="send" className="float-left text-dark" onClick={handleSubmit(handleSend)}>
                        <i className="fas fa-paper-plane"></i> {t('editor.notifications.send_now')}
                      </Button>
                    }
                    <Button className="sm text-dark" color="secondary-light" data-action="save-edit" onClick={handleSubmit(handleSave)}>
                      <i className="fas fa-clock"></i> {t('editor.notifications.schedule')}
                    </Button>
                  </div>
                </div>
                }
        </ListGroupItem>
    )
}

export function useSendTypesList() {
  const { t } = useTranslation();

  const sendTypesMap = useMemo(() => {
      return new Map([
          ['onetime', { label: t('editor.notifications.onetime'), value: 'onetime'}],
          ['recurrent', { label: t('editor.notifications.recurrent'), value: 'recurrent' }],
      ])
  }, [t])
  
  return { sendTypesMap, sendTypesArray: Array.from(sendTypesMap.values()) }
}

function SendTypesSelect({jurisdiction, ...props}) {
 const { sendTypesArray } = useSendTypesList()

  return (
      <Select
          styles={reactSelectStyles}
          options={sendTypesArray}
          {...props}
      />
  )
}
export function useFrequencyList() {
  const { t } = useTranslation();

  const frequencyMap = useMemo(() => {
      return new Map([
          ['daily', { label: t('editor.notifications.daily'), value: 'daily'}],
          ['weekly', { label: t('editor.notifications.weekly'), value: 'weekly' }],
          ['monthly', { label: t('editor.notifications.monthly'), value: 'monthly' }],
      ])
  }, [t])
  
  return { frequencyMap, frequencyArray: Array.from(frequencyMap.values()) }
}

function FrequencySelect(props) {
 const { frequencyArray } = useFrequencyList()

  return (
      <Select
          styles={reactSelectStyles}
          options={frequencyArray}
          {...props}
      />
  )
}

export function useTimeList({ start = 0, end = 24 } = {}) {
  const timesMap = useMemo(() => {
      const rangeArr = range(start, end);
      
      return new Map(rangeArr.map(r => {
        const value = zeroPad(r, 2);
        return [ value, { label: `${value}:00`, value } ]
      }))
  }, [start, end])
  
  return { timesMap, timesArray: Array.from(timesMap.values()) }
}

function TimesSelect({ startTime, endTime, ...props}) {
 const { timesArray } = useTimeList({ start: startTime, end: endTime })

  return (
      <Select
          styles={reactSelectStyles}
          options={timesArray}
          {...props}
      />
  )
}

export function useDayList({ start = 1, end = 31 } = {}) {
  const daysMap = useMemo(() => {
      const rangeArr = range(start, end);
      
      return new Map(rangeArr.map(r => {
        const value = zeroPad(r, 2);
        return [ value, { label: value, value } ]
      }))
  }, [start, end])
  
  return { daysMap, daysArray: Array.from(daysMap.values()) }
}

function DaysSelect({ startDay = 1, endDay = 31, ...props}) {
  const { daysArray } = useDayList({ start: startDay, end: endDay })

  return (
      <Select
          styles={reactSelectStyles}
          options={daysArray}
          {...props}
      />
  )
}

export function useWeekList() {
  const { t } = useTranslation();

  const weeksMap = useMemo(() => {
      return new Map([
        [ '01', { label: t('editor.notifications.monday'), value: '01' } ],
        [ '02', { label: t('editor.notifications.tuesday'), value: '02' } ],
        [ '03', { label: t('editor.notifications.wednesday'), value: '03' } ],
        [ '04', { label: t('editor.notifications.thursday'), value: '04' } ],
        [ '05', { label: t('editor.notifications.friday'), value: '05' } ],
      ]);
  }, [t])
  
  return { weeksMap, weeksArray: Array.from(weeksMap.values()) }
}

function WeekSelect({ startDay = 1, endDay = 31, ...props}) {
 const { weeksArray } = useWeekList({ start: startDay, end: endDay })

  return (
      <Select
          styles={reactSelectStyles}
          options={weeksArray}
          {...props}
      />
  )
}

const InputTagRoot = styled.div`
  background: white;
  border: 1px solid #d6d6d6;
  border-radius: 2px;
  display: flex;
  flex-wrap: wrap;
  padding: 5px 5px 0;
`

const InputTagContainer = styled.ul`
  display: inline-flex;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;
  width: 100%;
`

const InputTagItem = styled.li`
  align-items: center;
  background: #f47b33;
  border-radius: 2px;
  color: white;
  display: flex;
  font-weight: 300;
  list-style: none;
  margin-bottom: 5px;
  margin-right: 5px;
  padding: 5px 10px;
  &.input-tag__tags__input {
    background: none;
    flex-grow: 1;
    padding: 0;
    color: $dark;
    input:focus {
      outline: 0;
    }
    input {
      border: none;
      width: 100%;
    }
  
  }
  .button {
    align-items: center;
    appearance: none;
    color: white;
    cursor: pointer;
    display: inline-flex;
    font-size: 12px;
    justify-content: center;
    line-height: 0;
    margin-left: 8px;
    padding: 0;
}
`

function InputTag({ className, tags = [], placeholder, onChange }) {
  const tagInput = useRef();
  
  const removeTag = (i) => {
    const newTags = [ ...tags ];
    newTags.splice(i, 1);

    onChange(newTags)
  };

  const inputKeyDown = (e) => {
    const val = e.target.value;
    if (e.key === 'Enter' && val) {
      if (tags.find(tag => tag.toLowerCase() === val.toLowerCase())) {
        return;
      }
      onChange([...tags, val]);
      tagInput.current.value = null;
    } else if (e.key === 'Backspace' && !val) {
      removeTag(tags.length - 1);
    }
  };


  return (
    <InputTagRoot className="input-tag">
      <InputTagContainer className="input-tag__tags">
        { tags.map((tag, i) => (
          <InputTagItem key={tag}>
            {tag}
              <i className="ml-1 fas fa-times-circle button" onClick={() => { removeTag(i); }} />
            {/* <button type="button" >+</button> */}
          </InputTagItem>
        ))}
        <InputTagItem className="input-tag__tags__input">
          <input type="text" onKeyDown={inputKeyDown} placeholder={placeholder}  ref={tagInput} />
        </InputTagItem>
      </InputTagContainer>
    </InputTagRoot>
  );
}
